import React, { useEffect, useMemo, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
    ArrowLeft, Plus, Trash2, Save, TrendingUp, AlertTriangle, Pencil,
    Download, Upload, Search,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import SharePopover from "../../components/SharePopover";
import DocAttachments from "../../components/DocAttachments";

/**
 * Distributor Hub — at-a-glance view of business / payments / discount tiers / bonus.
 *
 * Accessible by:
 *   - admin / editor / Accounts & Billing employee → full read + write
 *   - employee with `mr` capability → read-only, filtered to their territory
 *
 * This single page powers both Admin AND Employee routes; the backend response's
 * `mode` field tells us which UI mode to render (full vs mr read-only).
 */
export default function DistributorHub() {
    const [data, setData] = useState(null);
    const [search, setSearch] = useState("");
    const [openId, setOpenId] = useState(null);

    const load = async () => {
        try {
            const { data: payload } = await api.get("/distributor-hub");
            setData(payload);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load distributor hub");
            setData({ mode: "denied", rows: [], totals: {} });
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (!data?.rows) return [];
        const q = (search || "").toLowerCase().trim();
        if (!q) return data.rows;
        return data.rows.filter((r) => [r.business_name, r.contact_name, r.email, r.phone, r.city, r.state].some((v) => (v || "").toLowerCase().includes(q)));
    }, [data, search]);

    if (!data) return <div className="text-sm text-stone-500" data-testid="hub-loading">Loading distributor hub…</div>;

    const canEdit = data.mode === "full";
    const mrReadonly = data.mode === "mr";

    return (
        <div data-testid="distributor-hub">
            <Toaster position="top-center" />
            <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700">B2B operations</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Distributor Hub</h1>
                    <p className="text-sm text-stone-500 mt-1">
                        {canEdit
                            ? "Edit discount tiers, log bonus structures, see what every distributor is owed at a glance."
                            : "Read-only view of distributors in your territory. Contact accounts to update tiers."}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button onClick={() => exportCsv(filtered)} variant="outline" className="rounded-none border-stone-300" data-testid="hub-export"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
                    <SharePopover
                        text={`Distributor business snapshot · ${filtered.length} distributors · YTD billed ${formatINR(data.totals?.ytd_billed || 0)} · Outstanding ${formatINR(data.totals?.ytd_outstanding || 0)}`}
                        subject="Distributor Hub snapshot"
                        label="Share snapshot"
                        testId="hub-share"
                    />
                </div>
            </div>

            {mrReadonly && (
                <div className="bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    Showing <strong className="mx-1">{filtered.length}</strong> distributor{filtered.length === 1 ? "" : "s"} in your assigned territory. Discount-tier edits are restricted to admin / editor / Accounts & Billing.
                </div>
            )}

            {data.mode === "mr_no_territory" && (
                <div className="bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-900">
                    Your MR territory isn't set yet. Ask the admin to set your states in <strong>Employee &rsaquo; My Designation</strong> so the hub can scope to your distributors.
                </div>
            )}

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="hub-kpis">
                <KPI label="Distributors" value={data.totals?.distributors ?? 0} accent="sky" />
                <KPI label="YTD Billed" value={formatINR(data.totals?.ytd_billed || 0)} accent="stone" />
                <KPI label="YTD Paid" value={formatINR(data.totals?.ytd_paid || 0)} accent="emerald" />
                <KPI label="Outstanding" value={formatINR(data.totals?.ytd_outstanding || 0)} accent="amber" />
            </div>

            {/* Search */}
            <div className="relative max-w-md mb-4">
                <Search className="w-4 h-4 absolute left-3 top-3 text-stone-400 pointer-events-none" />
                <Input
                    placeholder="Search by name / city / phone / email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="rounded-none border-stone-300 pl-9"
                    data-testid="hub-search"
                />
            </div>

            {/* Table */}
            <div className="bg-white border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 text-left text-[11px] uppercase tracking-widest text-stone-500">
                            <th className="p-3">Distributor</th>
                            <th className="p-3">Location</th>
                            <th className="p-3 text-right">YTD billed</th>
                            <th className="p-3 text-right">YTD paid</th>
                            <th className="p-3 text-right">Outstanding</th>
                            <th className="p-3">Discount</th>
                            <th className="p-3">Bonus</th>
                            <th className="p-3 w-24"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={8} className="text-center text-stone-500 py-10">No distributors match.</td></tr>
                        ) : filtered.map((r) => (
                            <tr key={r.id} className="border-t border-stone-100 hover:bg-sky-50/40" data-testid={`hub-row-${r.id}`}>
                                <td className="p-3">
                                    <div className="font-medium text-stone-900">{r.business_name || r.contact_name}</div>
                                    <div className="text-xs text-stone-500">{r.contact_name} · {r.phone || r.email}</div>
                                </td>
                                <td className="p-3 text-stone-700">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                                <td className="p-3 text-right font-medium">{formatINR(r.ytd_billed)}</td>
                                <td className="p-3 text-right text-emerald-700">{formatINR(r.ytd_paid)}</td>
                                <td className={`p-3 text-right font-semibold ${r.ytd_outstanding > 0 ? "text-amber-700" : "text-stone-500"}`}>
                                    {formatINR(r.ytd_outstanding)}
                                </td>
                                <td className="p-3">
                                    <Badge variant="outline" className="rounded-none border-stone-300 text-stone-700 font-normal">
                                        {r.tier_count > 0 ? `${r.tier_count} tier${r.tier_count === 1 ? "" : "s"} · ${r.discount_tier_mode}` : `Flat ${r.default_discount_pct || 0}%`}
                                    </Badge>
                                    {r.cash_discount_pct > 0 && (
                                        <div className="text-[10px] text-emerald-700 mt-1">+{r.cash_discount_pct}% cash</div>
                                    )}
                                </td>
                                <td className="p-3 text-xs text-stone-600 max-w-[18ch] truncate" title={r.bonus_notes || ""}>{r.bonus_notes || "—"}</td>
                                <td className="p-3">
                                    <Button variant="ghost" size="sm" onClick={() => setOpenId(r.id)} className="rounded-none text-sky-700" data-testid={`hub-open-${r.id}`}>
                                        {canEdit ? <><Pencil className="w-3.5 h-3.5 mr-1.5" />Manage</> : <>View</>}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {openId && (
                <DistributorDrawer
                    id={openId}
                    onClose={() => setOpenId(null)}
                    onSaved={() => { setOpenId(null); load(); }}
                />
            )}
        </div>
    );
}

function KPI({ label, value, accent }) {
    const tone = {
        sky: "border-sky-200 text-sky-700",
        stone: "border-stone-200 text-stone-700",
        emerald: "border-emerald-200 text-emerald-700",
        amber: "border-amber-200 text-amber-700",
    }[accent] || "border-stone-200 text-stone-700";
    return (
        <div className={`bg-white border ${tone} p-4`} data-testid={`hub-kpi-${accent}`}>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className="mt-1 text-xl font-medium">{value}</div>
        </div>
    );
}

function DistributorDrawer({ id, onClose, onSaved }) {
    const [detail, setDetail] = useState(null);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState(null);

    useEffect(() => {
        api.get(`/distributor-hub/${id}`).then(({ data }) => {
            setDetail(data);
            setForm({
                discount_tier_mode: data.distributor.discount_tier_mode || "subtotal",
                discount_tiers: data.distributor.discount_tiers || [],
                default_discount_pct: data.distributor.default_discount_pct || 0,
                cash_discount_pct: data.distributor.cash_discount_pct || 0,
                bonus_notes: data.distributor.bonus_notes || "",
                billing_notes: data.distributor.billing_notes || "",
            });
        }).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load distributor"));
    }, [id]);

    const save = async () => {
        if (!form) return;
        setSaving(true);
        try {
            await api.put(`/distributor-hub/${id}/tiers`, form);
            toast.success("Discount tiers + bonus saved");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const addTier = () => setForm((f) => ({
        ...f,
        discount_tiers: [...(f.discount_tiers || []), { label: "", min_subtotal: 0, min_ytd: 0, discount_pct: 0 }],
    }));
    const updateTier = (i, patch) => setForm((f) => ({
        ...f,
        discount_tiers: f.discount_tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t),
    }));
    const removeTier = (i) => setForm((f) => ({
        ...f,
        discount_tiers: f.discount_tiers.filter((_, idx) => idx !== i),
    }));

    if (!detail) return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="rounded-none max-w-md"><div className="text-sm text-stone-500 py-8 text-center">Loading distributor details…</div></DialogContent>
        </Dialog>
    );

    const d = detail.distributor;
    const k = detail.kpis;
    const canEdit = !!detail.can_edit;

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="rounded-none max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="hub-drawer">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">
                        <button onClick={onClose} className="text-sky-700 mr-2 align-middle"><ArrowLeft className="w-4 h-4 inline" /></button>
                        {d.business_name || d.contact_name}
                    </DialogTitle>
                    <div className="text-xs text-stone-500">{d.contact_name} · {d.email} · {d.phone} · {[d.city, d.state, d.pincode].filter(Boolean).join(", ")}</div>
                </DialogHeader>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                    <KPI label="YTD Billed" value={formatINR(k.ytd_billed)} accent="stone" />
                    <KPI label="YTD Paid" value={formatINR(k.ytd_paid)} accent="emerald" />
                    <KPI label="Outstanding" value={formatINR(k.ytd_outstanding)} accent="amber" />
                    <KPI label="Orders FY" value={k.orders} accent="sky" />
                </div>

                {/* Discount tier editor / viewer */}
                <section className="mt-6 border border-stone-200 bg-white">
                    <header className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
                        <h3 className="font-serif text-lg text-stone-900">Discount tiers</h3>
                        {canEdit && !editing && (
                            <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-none border-sky-300 text-sky-700" data-testid="hub-edit-tiers"><Pencil className="w-3.5 h-3.5 mr-1.5" />Edit</Button>
                        )}
                    </header>
                    <div className="p-4 space-y-3">
                        {editing && canEdit ? (
                            <>
                                <div className="grid sm:grid-cols-3 gap-3">
                                    <div>
                                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">Tier match mode</Label>
                                        <select
                                            value={form.discount_tier_mode}
                                            onChange={(e) => setForm({ ...form, discount_tier_mode: e.target.value })}
                                            className="rounded-none border border-stone-300 mt-1 h-10 w-full px-2 text-sm bg-white"
                                            data-testid="hub-tier-mode"
                                        >
                                            <option value="subtotal">Order subtotal</option>
                                            <option value="ytd_business">YTD business</option>
                                            <option value="both">Both must apply</option>
                                        </select>
                                    </div>
                                    <NumField label="Flat default discount %" value={form.default_discount_pct} on={(v) => setForm({ ...form, default_discount_pct: v })} tid="hub-default-disc" />
                                    <NumField label="Default cash discount %" value={form.cash_discount_pct} on={(v) => setForm({ ...form, cash_discount_pct: v })} tid="hub-cash-disc" />
                                </div>

                                <div className="space-y-2">
                                    {(form.discount_tiers || []).length === 0
                                        ? <div className="text-xs text-stone-500 border border-dashed border-stone-300 p-3 text-center">No tiers — billing will apply the flat default discount above.</div>
                                        : form.discount_tiers.map((t, i) => (
                                            <div key={i} className="grid grid-cols-12 gap-2 items-end border border-stone-200 p-2" data-testid={`hub-tier-${i}`}>
                                                <div className="col-span-12 sm:col-span-3">
                                                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">Label</Label>
                                                    <Input value={t.label} onChange={(e) => updateTier(i, { label: e.target.value })} placeholder="Silver" className="rounded-none border-stone-300 mt-1" data-testid={`hub-tier-label-${i}`} />
                                                </div>
                                                <NumField span="3" label="Min subtotal (Rs.)" value={t.min_subtotal} on={(v) => updateTier(i, { min_subtotal: v })} tid={`hub-tier-min-${i}`} />
                                                <NumField span="3" label="Min YTD business (Rs.)" value={t.min_ytd} on={(v) => updateTier(i, { min_ytd: v })} tid={`hub-tier-ytd-${i}`} />
                                                <NumField span="2" label="Discount %" value={t.discount_pct} on={(v) => updateTier(i, { discount_pct: v })} tid={`hub-tier-pct-${i}`} />
                                                <div className="col-span-12 sm:col-span-1 flex justify-end">
                                                    <Button variant="ghost" size="sm" onClick={() => removeTier(i)} className="rounded-none text-rose-700" data-testid={`hub-tier-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
                                                </div>
                                            </div>
                                        ))}
                                    <Button onClick={addTier} variant="outline" size="sm" className="rounded-none border-sky-300 text-sky-700" data-testid="hub-tier-add"><Plus className="w-3.5 h-3.5 mr-1.5" />Add tier</Button>
                                </div>

                                <div>
                                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">Bonus structure (manual — applied by Accounts at billing)</Label>
                                    <Textarea
                                        value={form.bonus_notes}
                                        onChange={(e) => setForm({ ...form, bonus_notes: e.target.value })}
                                        placeholder={"e.g.\n2 free Arnica 30C for every Rs. 5000 billed\nDiwali: +5% cash discount on outstanding ≤ 30 days"}
                                        rows={4}
                                        className="rounded-none border-stone-300 mt-1 font-mono text-xs"
                                        data-testid="hub-bonus-notes"
                                    />
                                </div>

                                <div>
                                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">Billing notes (surfaced on every new invoice for this distributor)</Label>
                                    <Textarea
                                        value={form.billing_notes}
                                        onChange={(e) => setForm({ ...form, billing_notes: e.target.value })}
                                        placeholder="e.g. Always email invoice copy to accounts@buyer.com — courier via DTDC only"
                                        rows={2}
                                        className="rounded-none border-stone-300 mt-1 text-xs"
                                        data-testid="hub-billing-notes"
                                    />
                                </div>

                                <div className="flex justify-end gap-2 pt-2">
                                    <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-none" data-testid="hub-cancel-edit">Cancel</Button>
                                    <Button onClick={save} disabled={saving} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="hub-save-tiers"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save changes"}</Button>
                                </div>
                            </>
                        ) : (
                            <ReadOnlyTiers d={d} />
                        )}
                    </div>
                </section>

                {/* Monthly business */}
                {(detail.monthly || []).length > 0 && (
                    <section className="mt-4 border border-stone-200 bg-white">
                        <header className="px-4 py-3 border-b border-stone-100 bg-stone-50">
                            <h3 className="font-serif text-lg text-stone-900 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-700" />Monthly business</h3>
                        </header>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-stone-500 bg-stone-50">
                                    <th className="p-2 pl-4">Month</th>
                                    <th className="p-2 text-right">Billed</th>
                                    <th className="p-2 text-right pr-4">Paid</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.monthly.map((m) => (
                                    <tr key={m.month} className="border-t border-stone-100" data-testid={`hub-month-${m.month}`}>
                                        <td className="p-2 pl-4">{m.month}</td>
                                        <td className="p-2 text-right">{formatINR(m.billed)}</td>
                                        <td className="p-2 text-right pr-4 text-emerald-700">{formatINR(m.paid)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {/* Recent orders + payments */}
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <RecentOrders rows={detail.recent_orders} />
                    <RecentPayments rows={detail.recent_payments} />
                </div>

                {/* Attachments (Accounts & Admin can upload supporting docs per-distributor) */}
                <section className="mt-4 border border-stone-200 bg-white">
                    <header className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
                        <h3 className="font-serif text-lg text-stone-900 flex items-center gap-2"><Upload className="w-4 h-4 text-sky-700" />Supporting documents</h3>
                    </header>
                    <div className="p-4">
                        <DocAttachments scope="distributor" scopeId={id} canEdit={canEdit} testIdPrefix="hub-att" />
                    </div>
                </section>
            </DialogContent>
        </Dialog>
    );
}

function ReadOnlyTiers({ d }) {
    const tiers = d.discount_tiers || [];
    return (
        <div data-testid="hub-tiers-readonly">
            <div className="flex flex-wrap gap-3 text-xs text-stone-700 mb-3">
                <Badge variant="outline" className="rounded-none border-sky-300 text-sky-700">Mode: {d.discount_tier_mode || "subtotal"}</Badge>
                {d.default_discount_pct > 0 && <Badge variant="outline" className="rounded-none border-stone-300">Default {d.default_discount_pct}%</Badge>}
                {d.cash_discount_pct > 0 && <Badge variant="outline" className="rounded-none border-emerald-300 text-emerald-700">Cash discount {d.cash_discount_pct}%</Badge>}
            </div>
            {tiers.length === 0 ? (
                <div className="text-xs text-stone-500 italic">No tiers configured — invoices apply the flat default discount above.</div>
            ) : (
                <table className="w-full text-sm border border-stone-200">
                    <thead><tr className="bg-stone-50 text-left text-[10px] uppercase tracking-widest text-stone-500">
                        <th className="p-2 pl-3">Label</th><th className="p-2 text-right">Min subtotal</th><th className="p-2 text-right">Min YTD</th><th className="p-2 text-right pr-3">Discount %</th>
                    </tr></thead>
                    <tbody>
                        {tiers.map((t, i) => (
                            <tr key={t.label || `tier-${i}`} className="border-t border-stone-100">
                                <td className="p-2 pl-3 font-medium">{t.label}</td>
                                <td className="p-2 text-right">{formatINR(t.min_subtotal || 0)}</td>
                                <td className="p-2 text-right">{formatINR(t.min_ytd || 0)}</td>
                                <td className="p-2 text-right pr-3 text-sky-700 font-semibold">{t.discount_pct || 0}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {d.bonus_notes && (
                <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Bonus structure</div>
                    <pre className="text-xs text-stone-700 whitespace-pre-wrap font-sans bg-stone-50 p-3 border border-stone-200">{d.bonus_notes}</pre>
                </div>
            )}
            {d.billing_notes && (
                <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Billing notes</div>
                    <pre className="text-xs text-stone-700 whitespace-pre-wrap font-sans bg-amber-50 border border-amber-200 p-3">{d.billing_notes}</pre>
                </div>
            )}
        </div>
    );
}

function RecentOrders({ rows }) {
    return (
        <section className="border border-stone-200 bg-white">
            <header className="px-4 py-3 border-b border-stone-100 bg-stone-50">
                <h3 className="font-serif text-lg text-stone-900">Recent invoices</h3>
            </header>
            <table className="w-full text-xs">
                <thead><tr className="text-left text-[10px] uppercase tracking-widest text-stone-500"><th className="p-2 pl-4">Date</th><th className="p-2">Invoice #</th><th className="p-2 text-right pr-4">Total</th></tr></thead>
                <tbody>
                    {(rows || []).length === 0
                        ? <tr><td colSpan={3} className="text-center text-stone-500 py-4">No orders yet.</td></tr>
                        : rows.map((r) => (
                            <tr key={r.order_id} className="border-t border-stone-100" data-testid={`hub-inv-${r.order_id}`}>
                                <td className="p-2 pl-4">{r.date}</td>
                                <td className="p-2">{r.invoice_number || <span className="text-stone-400">Order only</span>}</td>
                                <td className="p-2 text-right pr-4 font-medium">{formatINR(r.invoice_total ?? r.order_total)}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </section>
    );
}

function RecentPayments({ rows }) {
    return (
        <section className="border border-stone-200 bg-white">
            <header className="px-4 py-3 border-b border-stone-100 bg-stone-50">
                <h3 className="font-serif text-lg text-stone-900">Recent payments</h3>
            </header>
            <table className="w-full text-xs">
                <thead><tr className="text-left text-[10px] uppercase tracking-widest text-stone-500"><th className="p-2 pl-4">Date</th><th className="p-2">Method · Ref</th><th className="p-2 text-right pr-4">Amount</th></tr></thead>
                <tbody>
                    {(rows || []).length === 0
                        ? <tr><td colSpan={3} className="text-center text-stone-500 py-4">No payments recorded yet.</td></tr>
                        : rows.map((r, i) => (
                            <tr key={r.id || `${r.date}-${r.ref}-${i}`} className="border-t border-stone-100" data-testid={`hub-pay-${i}`}>
                                <td className="p-2 pl-4">{r.date}</td>
                                <td className="p-2">{r.method}{r.ref ? ` · ${r.ref}` : ""}</td>
                                <td className="p-2 text-right pr-4 font-medium text-emerald-700">{formatINR(r.amount)}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </section>
    );
}

const NumField = ({ label, value, on, tid, span }) => (
    <div className={span ? `col-span-12 sm:col-span-${span}` : ""}>
        <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
        <Input type="number" step="0.5" value={value ?? 0} onChange={(e) => on(parseFloat(e.target.value || 0))} className="rounded-none border-stone-300 mt-1" data-testid={tid} />
    </div>
);

function exportCsv(rows) {
    const headers = ["Business", "Contact", "Phone", "Email", "City", "State", "YTD Billed", "YTD Paid", "Outstanding", "Tier mode", "Tiers", "Default disc %", "Cash disc %", "Bonus notes"];
    const data = rows.map((r) => [
        r.business_name, r.contact_name, r.phone, r.email, r.city, r.state,
        r.ytd_billed, r.ytd_paid, r.ytd_outstanding,
        r.discount_tier_mode, r.tier_count, r.default_discount_pct, r.cash_discount_pct, (r.bonus_notes || "").replace(/\n/g, " "),
    ]);
    const csv = [headers, ...data].map((row) => row.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `distributor-hub-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success("CSV exported");
}
